import { isCustomerLoggedIn, is_set_object } from "$lib/js/helpers"
import { getCustomerToken } from "$lib/js/magento/cart/cart"
import { storeCustomerInfo, storeCustomerToken, storeProductToAdd, storeSnackbar, storeWishlists, storeActiveWishlist } from "$lib/js/stores"
import { get } from "svelte/store"
import { gt } from 'svelte-i18n-lingui'
import { type SiteScope } from '$lib/js/helpers/i18n'
import type { CustomerToken } from "$lib/js/magento/types"

export type Wishlist = {
	id: string,
	user_email: string,
	skus: Array<string>,
	name: string,
	status: string,
	slug: string
}

// Check if product is in wishlist on component mount
export async function checkWishlistStatus({ scope }: { scope: SiteScope }, customerToken?: CustomerToken) {
	let token

	if (customerToken) {
		token = get(storeCustomerToken)
	} else {
		token = await getCustomerToken()
	}

	if (!isCustomerLoggedIn(token)) {
		return
	}

	try {
		const userEmail = get(storeCustomerInfo)?.email
		// const userEmail = 'vazkez242@gmail.com'

		if (!userEmail) {
			return
		}

		const response = await fetch(`/${scope}/endpoints/wishlist?email=${encodeURIComponent(userEmail)}`)
		const result = await response.json()

		if (!result.success) {
			throw new Error(result.error || 'Failed to fetch wishlist')
		}

		const wishlists = result.data
		storeWishlists.set(wishlists)
		const wishlist = wishlists.find((wishlist: Wishlist) => wishlist.status === 'active')
		storeActiveWishlist.set(wishlist)
		const currentSkus = wishlist?.skus || []
		const isInWishlist = currentSkus.includes(get(storeProductToAdd)?.sku)
		return isInWishlist
	} catch (error) {
		console.error('Failed to check wishlist status:', error)
	}
}

// Check if product is in wishlist on component mount
export async function getUserWishlists({ scope }: { scope: SiteScope }, customerToken?: CustomerToken | null) {
	let token

	if (customerToken && is_set_object(get(storeCustomerToken))) {
		token = get(storeCustomerToken)
	} else {
		token = await getCustomerToken()
	}

	if (!isCustomerLoggedIn(token)) {
		return
	}

	try {
		const userEmail = get(storeCustomerInfo)?.email
		// const userEmail = 'vazkez242@gmail.com'

		if (!userEmail) {
			return
		}

		const response = await fetch(`/${scope}/endpoints/wishlist?email=${encodeURIComponent(userEmail)}`)
		const result = await response.json()

		if (!result.success) {
			throw new Error(result.error || 'Failed to fetch wishlist')
		}

		const wishlists = result.data
		return wishlists
	} catch (error) {
		console.error('Failed to check wishlist status:', error)
		return []
	}
}

export async function getWishlistProducts(skus: string[], { scope }: { scope: SiteScope }, customerToken?: CustomerToken | null) {
	if (!skus.length) {
		return []
	}

	const response = await fetch(`/${scope}/endpoints/sku_products`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(skus),
	})
	const data = await response.json()
	return data || []
}


export async function createNewWishlist(name: string, email: string, scope: string) {
	try {
		const response = await fetch(`/${scope}/endpoints/wishlist/create`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				name,
				email,
			}),
		})

		const result = await response.json()

		if (!result.success) {
			throw new Error(result.error || 'Failed to create wishlist')
		}

		const snackbar = get(storeSnackbar)
		snackbar.message = gt`Wishlist created successfully`
		snackbar.show = true
		storeSnackbar.set(snackbar)

		return result.data
	} catch (error) {
		console.error('Failed to create wishlist:', error)

		const snackbar = get(storeSnackbar)
		snackbar.message = gt`Failed to create wishlist`
		snackbar.show = true
		storeSnackbar.set(snackbar)

		throw error
	}
} 